import { useCallback, useMemo, useRef, useState } from 'react';

import mixpanel from 'mixpanel-browser';

import useSetUserAttributes from 'hooks/useSetUserAttributes';
import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';

import { INITIAL_ANSWERS, QUESTIONNAIRE_VERSION } from './constants';
import { QuestionnaireAnswers, QuestionnaireStep, QuestionnaireTrigger } from './types';

const useQuestionnaire = (trigger: QuestionnaireTrigger) => {
  const { mutateAsync: setUserAttributes } = useSetUserAttributes();
  const [currentStep, setCurrentStep] = useState<QuestionnaireStep>(1);
  const [answers, setAnswers] = useState<QuestionnaireAnswers>({ ...INITIAL_ANSWERS });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [hasError, setHasError] = useState(false);
  const startTimeRef = useRef<number>(Date.now());

  const userType = trigger === 'first_login' ? 'new' : 'existing';

  const setAnswer = useCallback(<K extends keyof QuestionnaireAnswers>(field: K, value: QuestionnaireAnswers[K]) => {
    setAnswers((prev) => ({ ...prev, [field]: value }));
  }, []);

  const canProceed = useMemo(() => {
    switch (currentStep) {
      case 1:
        return answers.experienceLevel !== null;
      case 2:
        return answers.useCases.length > 0;
      case 3:
        return answers.industry !== null;
      default:
        return false;
    }
  }, [currentStep, answers.experienceLevel, answers.useCases.length, answers.industry]);

  const trackStepCompleted = useCallback(
    (step: QuestionnaireStep) => {
      let value: string | string[];
      switch (step) {
        case 1:
          value = answers.experienceLevel ?? '';
          break;
        case 2:
          value = answers.useCases;
          break;
        case 3:
          value = answers.industry ?? '';
          break;
      }
      mixpanel.track(MIXPANEL_EVENTS.QUESTIONNAIRE_STEP_COMPLETED, { step, value, user_type: userType });
    },
    [answers.experienceLevel, answers.useCases, answers.industry, userType]
  );

  const nextStep = useCallback(() => {
    if (!canProceed) return;
    trackStepCompleted(currentStep);

    const next = (currentStep + 1) as QuestionnaireStep;
    setCurrentStep(next);
    mixpanel.track(MIXPANEL_EVENTS.QUESTIONNAIRE_STEP_VIEWED, { step: next, user_type: userType });
  }, [canProceed, currentStep, trackStepCompleted, userType]);

  const prevStep = useCallback(() => {
    if (currentStep <= 1) return;
    mixpanel.track(MIXPANEL_EVENTS.QUESTIONNAIRE_BACK_CLICKED, { from_step: currentStep, user_type: userType });

    const prev = (currentStep - 1) as QuestionnaireStep;
    setCurrentStep(prev);
  }, [currentStep, userType]);

  const submit = useCallback(async () => {
    if (!canProceed) return;
    trackStepCompleted(currentStep);
    setIsSubmitting(true);
    setHasError(false);

    try {
      const otherText = answers.useCasesOther.trim();
      const useCases = answers.useCases.includes('other')
        ? [...answers.useCases.filter((v) => v !== 'other'), ...(otherText ? [`other: ${otherText}`] : ['other'])]
        : answers.useCases;

      mixpanel.people.set({
        experience_level: answers.experienceLevel,
        use_cases: useCases,
        industry: answers.industry,
        user_type: userType,
        questionnaire_completed_at: new Date().toISOString(),
        questionnaire_version: QUESTIONNAIRE_VERSION,
      });

      mixpanel.track(MIXPANEL_EVENTS.QUESTIONNAIRE_COMPLETED, {
        experience_level: answers.experienceLevel,
        use_cases: useCases,
        industry: answers.industry,
        time_to_complete_ms: Date.now() - startTimeRef.current,
        trigger,
        user_type: userType,
      });

      await setUserAttributes({
        questionnaire_completed: true,
        first_session_completed: true,
        use_cases: useCases,
        experience_level: answers.experienceLevel ?? undefined,
        industry: answers.industry ?? undefined,
      });

      setIsComplete(true);
    } catch {
      setHasError(true);
    } finally {
      setIsSubmitting(false);
    }
  }, [canProceed, trackStepCompleted, currentStep, answers, trigger, userType, setUserAttributes]);

  const abandon = useCallback(() => {
    mixpanel.track(MIXPANEL_EVENTS.QUESTIONNAIRE_ABANDONED, {
      last_step_viewed: currentStep,
      trigger,
      user_type: userType,
    });
  }, [currentStep, trigger, userType]);

  const reset = useCallback(() => {
    setCurrentStep(1);
    setAnswers({ ...INITIAL_ANSWERS });
    setIsSubmitting(false);
    setIsComplete(false);
    setHasError(false);
    startTimeRef.current = Date.now();
  }, []);

  const resetStartTime = useCallback(() => {
    startTimeRef.current = Date.now();
  }, []);

  return {
    currentStep,
    answers,
    isSubmitting,
    isComplete,
    hasError,
    canProceed,
    setAnswer,
    nextStep,
    prevStep,
    submit,
    abandon,
    reset,
    setHasError,
    resetStartTime,
  };
};

export default useQuestionnaire;
